// DatabaseSettingsFormsTab.jsx

import React, { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { moveOrderedLayoutById } from 'utilities/LayoutOrder';
import { isSystemTypeForm } from 'services/forms/TypeFormService';

// Components
import DatabaseSettingsAddButton from 'collection/settings/addbutton/DatabaseSettingsAddButton';
import DatabaseSettingsFormRow from 'collection/settings/forms/DatabaseSettingsFormRow';

/**
 * DatabaseSettingsFormsTab
 *
 * Renders form template management inside database settings.
 *
 * @param {boolean} [lockSystemForms=false] - When true, system forms are read-only rows.
 */
const DatabaseSettingsFormsTab = React.memo(({
  forms = [],
  isSaving = false,
  lockSystemForms = false,
  onAddForm,
  onDesignForm,
  onReorderForm,
  onSettingsForm,
}) => {
  // ----- CONTEXT ----- //

  const { theme } = useTheme();

  // ----- STATE ----- //

  const [orderedForms, setOrderedForms] = useState([]);
  const [draggingFormKey, setDraggingFormKey] = useState('');
  const [dragOverFormKey, setDragOverFormKey] = useState('');

  // ----- MEMOS ----- //

  const defaultFormKey = useMemo(() => {
    const defaultForm = forms.find((form) => form?.isDefault);
    return String(defaultForm?.key || '').trim();
  }, [forms]);

  // ----- EFFECTS ----- //

  useEffect(() => {
    setOrderedForms(forms);
  }, [forms]);

  // ----- HANDLERS ----- //

  const handleDragStart = useCallback((form, event) => {
    if (form?.isDefault) return;
    if (lockSystemForms && isSystemTypeForm(form)) return;

    const nextFormKey = String(form?.key || '').trim();
    if (!nextFormKey) return;

    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', nextFormKey);
    setDraggingFormKey(nextFormKey);
  }, [lockSystemForms]);

  const handleDragOver = useCallback((form, event) => {
    if (form?.isDefault) return;
    if (lockSystemForms && isSystemTypeForm(form)) return;

    const nextFormKey = String(form?.key || '').trim();
    if (!draggingFormKey || !nextFormKey || draggingFormKey === nextFormKey) return;

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverFormKey(nextFormKey);
  }, [draggingFormKey, lockSystemForms]);

  const handleDragEnd = useCallback(() => {
    setDraggingFormKey('');
    setDragOverFormKey('');
  }, []);

  const handleDrop = useCallback(async (form, event) => {
    event.preventDefault();

    const targetFormKey = String(form?.key || '').trim();
    const previousForms = orderedForms;
    handleDragEnd();

    if (!draggingFormKey || !targetFormKey || draggingFormKey === targetFormKey || form?.isDefault) {
      return;
    }

    if (lockSystemForms && isSystemTypeForm(form)) return;

    const draggedForm = orderedForms.find((entry) => (
      String(entry?.key || '').trim() === draggingFormKey
    ));
    if (lockSystemForms && isSystemTypeForm(draggedForm)) return;

    const nextForms = moveOrderedLayoutById(previousForms, draggingFormKey, targetFormKey);
    if (!nextForms) return;

    setOrderedForms(nextForms);

    try {
      await onReorderForm?.(draggingFormKey, targetFormKey);
    } catch {
      setOrderedForms(previousForms);
    }
  }, [draggingFormKey, handleDragEnd, lockSystemForms, onReorderForm, orderedForms]);

  // ----- RENDER ----- //

  return (
    <div className="database-modal-views">
      <DatabaseSettingsAddButton
        title="Add Form"
        onClick={() => onAddForm?.()}
      />

      <div className="database-modal-view-list">
        {orderedForms.map((form) => {
          const isLocked = lockSystemForms && (
            isSystemTypeForm(form)
            || (Boolean(form?.isDefault) && Boolean(form?.isVirtual))
          );

          return (
            <DatabaseSettingsFormRow
              key={form.key}
              badge={isLocked ? 'System' : ''}
              draggable={!form?.isDefault && !isLocked}
              iconKey={form?.isDefault ? 'dynamic_form' : 'edit_note'}
              isDefault={Boolean(form?.isDefault)}
              isDragging={draggingFormKey === form.key}
              isDropTarget={dragOverFormKey === form.key}
              label={form?.title || 'Untitled Form'}
              locked={isLocked}
              note={form?.isDefault ? 'Default form' : String(form?.description || '').trim()}
              showDragHandle={form?.key !== defaultFormKey && !isLocked}
              onDesignClick={() => {
                if (isLocked) return;
                onDesignForm?.(form);
              }}
              onDragEnd={handleDragEnd}
              onDragOver={(event) => handleDragOver(form, event)}
              onDragStart={(event) => handleDragStart(form, event)}
              onDrop={(event) => handleDrop(form, event)}
              onSettingsClick={() => {
                if (isLocked) return;
                onSettingsForm?.(form);
              }}
            />
          );
        })}
      </div>

      {isSaving ? (
        <div
          className="database-modal-empty-state"
          style={{
            color: theme.foregroundMuted,
          }}
        >
          Saving form template changes...
        </div>
      ) : null}
    </div>
  );
});

// Dev Tools
DatabaseSettingsFormsTab.displayName = 'DatabaseSettingsFormsTab';

export default DatabaseSettingsFormsTab;
